import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Container, Row, Col, Button, Form } from 'react-bootstrap';
import { ToastContainer, toast, Bounce } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useCookies } from "react-cookie";
import { useParams } from 'react-router-dom';
import { addToCart } from "../../../src/apis/cart";
import { getProductbyId } from "../../../src/apis/product";
import HomePageHeader from "../../components/HomePageHeader";
import HomePageFooter from "../../components/HomePageFooter";
import HomePageSearchBar from "../../components/HomePageSearchBar";
import StarBorderIcon from '@mui/icons-material/StarBorder';
import StarIcon from '@mui/icons-material/Star';
import styles from "./productdetail.module.css";
import ImageWithFallback from "../../components/ImageWithFallback";
import LoadingScreen from "../../components/LoadingScreen";

const ProductDetailPage = () => {
    const { t } = useTranslation();
    const { productId } = useParams();
    const [selectedSize, setSelectedSize] = useState(null);
    const [selectedLength, setSelectedLength] = useState(null);
    const [selectedColor, setSelectedColor] = useState(null);
    const [cookies] = useCookies(["access_token"]);
    const [product, setProduct] = useState(null);
    const [quantity, setQuantity] = useState(1);
    const [productCode, setProductCode] = useState(null);
    const [metadata, setMetadata] = useState(null);

    useEffect(() => {
        const fetchProductDetails = async () => {
            try {
                const token = cookies["access_token"];
                const result = await getProductbyId(productId, token);
                setProduct(result.data);
                // Set initial selections based on the first metadata entry
                const initialMetadata = result.data.productMetadata[0].metadata;
                setSelectedSize(initialMetadata.find(attr => attr.attribute === "Size Áo")?.value || null);
                setSelectedLength(initialMetadata.find(attr => attr.attribute === "Độ dài áo")?.value || null);
            } catch (error) {
                console.error("Failed to fetch product details:", error);
            }
        };
        fetchProductDetails();
    }, [productId, cookies]);

    const updateProductCode = useCallback(() => {
        if (!product || !selectedSize || !selectedLength) return;

        const matchingMetadata = product.productMetadata.find(metadata =>
            metadata.metadata.some(attr => attr.attribute === 'Size Áo' && attr.value === selectedSize) &&
            metadata.metadata.some(attr => attr.attribute === 'Độ dài áo' && attr.value === selectedLength)
        );

        if (matchingMetadata) {
            setProductCode(matchingMetadata.productCode)
            setMetadata(matchingMetadata)
            console.log("Matched Product Code:", matchingMetadata.productCode); 
        } else {
            setProductCode(null);
            console.log("No matching product code found.");
        }
    }, [product, selectedSize, selectedLength]);

    useEffect(() => {
        updateProductCode();
    }, [updateProductCode]);

    const handleAddToCart = async () => {
        updateProductCode();
        if (!productCode) {
            toast.error(t('Please select size, length'), {
                position: "top-center",
                autoClose: 1000,
            });
            return;
        }

        if (quantity > metadata?.stockQuantity) {
            toast.error(t('Quantity exceeds stock quantity'), {
                position: "top-center",
                autoClose: 1000,
            });
            return;
        }

        try {
            const token = cookies["access_token"];
            const result = await addToCart(productId, productCode, quantity, token);
            toast.success(t('Product added to cart'), {
                position: "top-center",
                autoClose: 1000,
            });
            console.log("Add to cart successful:", result);
        } catch (err) {
            console.error("Add to cart failed:", err);
            toast.error(t('Failed to add product to cart'), {
                position: "top-center",
                autoClose: 1000,
            });
        }
    };

    const renderStars = (value) => {
        return (
            <div>
                {[...Array(5)].map((_, index) =>
                    index < value ? (
                        <StarIcon key={index} style={{ color: '#ffb400' }} />
                    ) : (
                        <StarBorderIcon key={index} style={{ color: '#ffb400' }} />
                    )
                )}
            </div>
        );
    };

    if (!product) {
        return <LoadingScreen/>;
    }

    return (
        <div>
            <div className={styles.nav}>
                <HomePageHeader />
                <HomePageSearchBar />
            </div>
            <Container>
                <Row className="my-4">
                    <Col md={4}>
                        <div className={styles.product_image}>
                            <ImageWithFallback src={product.images?.[0]?.imageURL || "https://via.placeholder.com/400"} alt={product.name || "Product"} className="img-fluid mb-2" />
                            <div className={styles.thumbnail_images}>
                                {/* Add thumbnails if needed */}
                            </div>
                        </div>
                    </Col>
                    <Col md={1}></Col>
                    <Col md={7}>
                        <h4>{product.name || "Áo sơ mi cổ tròn"}</h4>
                        <div className={styles.rating}>
                            <span>
                                {renderStars(3)} {/* Replace with actual rating */}
                            </span>
                            <span>1280 Lượt mua</span>
                            <span>1280 Đánh giá</span>
                        </div>
                        <h3 className={styles.price}>{product.productMetadata[0]?.price.toLocaleString("vi-VN", { style: "currency", currency: "VND" }) || "499,000 đ"}</h3>

                        <div className={styles.block}>
                            <p className={styles.text}>{t('get_discount_code')}</p>
                            <div className={styles.discount_codes}>
                                <Button variant="outline-secondary">18% Giảm</Button>
                                <Button variant="outline-secondary">18% Giảm</Button>
                                <Button variant="outline-secondary">18% Giảm</Button>
                                <Button variant="link">Xem tất cả</Button>
                            </div>
                        </div>
                        <div className={styles.block}>
                            <p className={styles.text}>{t('return_policy')}</p>
                            <p className={styles.return_policy}>Trả hàng 15 ngày</p>
                        </div>
                        <Form>
                            <Form.Group controlId="formSize">
                                <Form.Label>Size</Form.Label>
                                <div>
                                    {Array.from(new Set(product.productMetadata.flatMap(metadata => metadata.metadata.filter(attr => attr.attribute === "Size Áo").map(attr => attr.value)))).map(size => (
                                        <Button
                                            key={size}
                                            variant="light"
                                            className={`${styles.button} ${selectedSize === size ? styles.selected : ''}`}
                                            onClick={() => setSelectedSize(size)}
                                        >
                                            {size}
                                        </Button>
                                    ))}
                                </div>
                            </Form.Group>

                            <Form.Group controlId="formLength">
                                <Form.Label>Độ dài áo</Form.Label>
                                <div>
                                    {Array.from(new Set(product.productMetadata.flatMap(metadata => metadata.metadata.filter(attr => attr.attribute === "Độ dài áo").map(attr => attr.value)))).map(length => (
                                        <Button
                                            key={length}
                                            variant="light"
                                            className={`${styles.button} ${selectedLength === length ? styles.selected : ''}`}
                                            onClick={() => setSelectedLength(length)}
                                        >
                                            {length}
                                        </Button>
                                    ))}
                                </div>
                            </Form.Group>

                            <Form.Group controlId="formColor">
                                <div>
                                    {Array.from(new Set(product.productMetadata.flatMap(metadata => metadata.metadata.filter(attr => attr.attribute === "Màu").map(attr => attr.value)))).map(color => (
                                        <Button
                                            key={color}
                                            variant="light"
                                            className={`${styles.button} ${selectedColor === color ? styles.selected : ''}`}
                                            onClick={() => setSelectedColor(color)}
                                        >
                                            {color}
                                        </Button>
                                    ))}
                                </div>
                            </Form.Group>
                            <Form.Group controlId="formQuantity">
                                <Form.Label>Số lượng</Form.Label>
                                <Form.Control
                                    type="number"
                                    value={quantity}
                                    min="1"
                                    max={metadata?.stockQuantity}
                                    onChange={(e) => setQuantity(Math.min(e.target.value, metadata?.stockQuantity))}
                                    style={{ width: "40%", textAlign: 'center', border: "1px solid black" }}
                                />
                            </Form.Group>
                            <Button className={styles.cus_button_1} onClick={handleAddToCart}>Thêm vào giỏ hàng</Button>
                            <Button className={styles.cus_button_2}>Mua Ngay</Button>
                        </Form>
                    </Col>
                </Row>
            </Container>
            <HomePageFooter />
            <ToastContainer
                position="top-right"
                autoClose={2000}
                hideProgressBar={false}
                newestOnTop={false}
                draggable
                pauseOnHover
                theme="light"
                transition={Bounce}
            />
        </div>
    );
};

export default ProductDetailPage;
